import { useState } from "react";
import { motion } from "framer-motion";
import SubHeader from "@/components/SubHeader";
import { Reveal, Chapter } from "@/components/Reveal";
import SimPackageBuilder from "@/components/sim/SimPackageBuilder";
import SimCustomerInquiry from "@/components/sim/SimCustomerInquiry";
import SimQuote from "@/components/sim/SimQuote";

const ROLES = [
  { id: "dmc", label: "DMC", flow: ["Manage", "Distribute", "Sell Direct"], journeys: [1, 4, 5] },
  { id: "distributor", label: "B2B Distributor", flow: ["Access", "Discover", "Rate", "Distribute"], journeys: [2] },
  { id: "agent", label: "Travel Agent", flow: ["Discover", "Inquire", "Quote", "Book — future"], journeys: [3, 5] },
];

const JOURNEYS = [
  {
    n: 1, title: "DMC Operations", phase: "Phase 1",
    steps: ["DMC Admin", "Create Destination", "Add Hotel", "Add Activity", "Create Transfer", "Create Package", "Configure Rate", "Publish"],
    sim: "builder",
  },
  {
    n: 2, title: "B2B Distribution", phase: "Phase 1",
    steps: ["DMC", "Select Partner", "Give Partner Access", "Partner logs in", "Searches destination", "Views products", "Views applicable rates", "Requests quote", "DMC receives request", "DMC responds"],
  },
  {
    n: 3, title: "Direct Travel Agent Channel", phase: "Phase 1",
    steps: ["DMC", "Enable Agent Channel", "Travel Agent discovers DMC", "Agent searches destination", "Views products", "Selects package", "Submits inquiry", "DMC receives inquiry", "DMC creates quote", "Agent responds"],
    future: ["Booking + Payment"],
    sim: "inquiry",
  },
  {
    n: 4, title: "Rate Management", phase: "Phase 1",
    steps: ["Supplier / DMC", "Update rate", "Set validity", "Set season", "Set markup", "Publish", "Authorized partners see latest rate"],
  },
  {
    n: 5, title: "Inquiry → Quote", phase: "Phase 1",
    steps: ["Agent / Customer", "Inquiry", "DMC Dashboard", "Assign Agent", "Review Requirements", "Build Quote", "Send Quote", "Customer / Agent Response", "Confirmed"],
    future: ["Booking", "Payment", "Voucher"],
    sim: "quote",
  },
];

export default function HowItWorks() {
  const [role, setRole] = useState("dmc");
  const activeRole = ROLES.find((r) => r.id === role);

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="how-it-works-page">
      <SubHeader />
      <main className="pb-24 pt-32">
        <section className="container-x">
          <Reveal><Chapter index={1} label="How It Works" /></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-8 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl">
              Five journeys. One connected platform.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-stone2 sm:text-lg">
              Pick your role and follow the exact workflow. Interactive steps use
              labeled sample data; roadmap steps are marked as future.
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-10 rounded-2xl border border-sand bg-white p-6" data-testid="role-selector">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-stone2">I am a…</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {ROLES.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setRole(r.id)}
                    data-testid={`role-${r.id}`}
                    className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                      role === r.id ? "bg-ink text-paper" : "border border-sand bg-paper text-stone2 hover:text-ink"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <motion.div key={role} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 flex flex-wrap items-center gap-2" data-testid="role-flow">
                {activeRole.flow.map((f, i) => (
                  <span key={f} className="flex items-center gap-2">
                    <span className="rounded-full bg-ink px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-wider text-paper">{f}</span>
                    {i < activeRole.flow.length - 1 && <span className="text-terra">→</span>}
                  </span>
                ))}
              </motion.div>
              <p className="mt-3 text-xs text-stone2">Your recommended {activeRole.journeys.length > 1 ? "journeys are" : "journey is"} highlighted below.</p>
            </div>
          </Reveal>
        </section>

        <section className="container-x mt-16 space-y-10">
          {JOURNEYS.map((j) => {
            const recommended = activeRole.journeys.includes(j.n);
            return (
              <Reveal key={j.n}>
                <div
                  data-testid={`journey-${j.n}`}
                  className={`rounded-2xl border bg-white p-6 transition-all duration-500 sm:p-8 ${
                    recommended ? "border-terra shadow-[0_30px_60px_-30px_rgba(196,154,90,0.5)]" : "border-sand"
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-xs font-bold tracking-[0.2em] text-terra">JOURNEY {String(j.n).padStart(2, "0")}</span>
                    <h2 className="font-serif text-2xl text-ink">{j.title}</h2>
                    <span className="rounded-full bg-emerald2/10 px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-wider text-emerald2">{j.phase}</span>
                    {recommended && (
                      <span className="rounded-full bg-terra px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-wider text-ink">For you</span>
                    )}
                  </div>
                  <ol className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-2.5">
                    {j.steps.map((s, i) => (
                      <li key={s} className="flex items-center gap-2">
                        <span className="rounded-lg border border-sand bg-paper px-3 py-2 text-xs font-semibold text-ink">{s}</span>
                        {i < j.steps.length - 1 && <span className="text-terra/70">→</span>}
                      </li>
                    ))}
                    {(j.future || []).map((f) => (
                      <li key={f} className="flex items-center gap-2">
                        <span className="text-terra/70">→</span>
                        <span className="rounded-lg border border-dashed border-terra/60 bg-terra/5 px-3 py-2 text-xs font-semibold text-stone2">
                          {f} <span className="ml-1 rounded-full bg-terra/20 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase text-terra">Roadmap</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                  {j.sim === "builder" && (
                    <div className="mt-8"><SimPackageBuilder /></div>
                  )}
                  {j.sim === "inquiry" && (
                    <div className="mt-8"><SimCustomerInquiry /></div>
                  )}
                  {j.sim === "quote" && (
                    <div className="mt-8"><SimQuote /></div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </section>

        <section className="container-x mt-16">
          <div className="rounded-2xl bg-ink p-10 text-center">
            <h2 className="font-serif text-3xl text-paper">Walk through it live.</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-paper/60">
              Try the full interactive product tour, or book a personalized demo for your DMC.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("tt:open-demo"))}
                data-testid="hiw-try-demo-btn"
                className="rounded-full border border-terra/60 px-7 py-3.5 text-sm font-bold text-terra transition-colors duration-300 hover:bg-terra hover:text-ink"
              >
                Try the Demo
              </button>
              <a href="/#demo" data-testid="hiw-book-demo-btn"
                className="rounded-full bg-paper px-7 py-3.5 text-sm font-bold text-ink transition-colors duration-300 hover:bg-terra">
                Book a Demo
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
